import React, { useState, useEffect } from 'react';
import {
  Activity,
  RefreshCw,
  Stethoscope,
  ExternalLink,
  CheckCircle,
  XCircle,
  Loader,
  Terminal,
} from 'lucide-react';
import { healthAPI, diagnosticsAPI } from '../services/api';
import { sessionsAPI } from '../services/sessionsAPI';

const API_BASE = process.env.REACT_APP_API_URL || '';

function Diagnostics() {
  const [health, setHealth] = useState(null);
  const [diag, setDiag] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState('');
  const [flTest, setFlTest] = useState(null);
  const [groqTest, setGroqTest] = useState(null);
  const [loading, setLoading] = useState({});
  const [error, setError] = useState(null);

  const setLoad = (key, v) => setLoading((prev) => ({ ...prev, [key]: v }));

  const fetchCore = async () => {
    setError(null);
    setLoad('core', true);
    try {
      const [h, d] = await Promise.all([healthAPI.check(), diagnosticsAPI.summary()]);
      setHealth(h.data);
      setDiag(d.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to load diagnostics');
    } finally {
      setLoad('core', false);
    }
  };

  const fetchSessions = async () => {
    try {
      const res = await sessionsAPI.getAll();
      const list = res.data.sessions || [];
      setSessions(list);
      if (!sessionId && list.length === 1) setSessionId(list[0].session_id);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchCore();
    fetchSessions();
  }, []);

  const runFreelancerTest = async () => {
    if (!sessionId) return;
    setFlTest(null);
    setLoad('fl', true);
    try {
      const res = await diagnosticsAPI.testFreelancer(sessionId);
      setFlTest(res.data);
    } catch (e) {
      setFlTest({ ok: false, error: e.response?.data?.detail || e.message });
    } finally {
      setLoad('fl', false);
    }
  };

  const runGroqTest = async () => {
    if (!sessionId) return;
    setGroqTest(null);
    setLoad('groq', true);
    try {
      const res = await diagnosticsAPI.testGroq(sessionId);
      setGroqTest(res.data);
    } catch (e) {
      setGroqTest({ ok: false, error: e.response?.data?.detail || e.message });
    } finally {
      setLoad('groq', false);
    }
  };

  const ResultBadge = ({ ok }) =>
    ok ? (
      <span className="inline-flex items-center gap-1 text-sm text-green-400">
        <CheckCircle className="w-4 h-4" /> OK
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 text-sm text-red-400">
        <XCircle className="w-4 h-4" /> Failed
      </span>
    );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-100 flex items-center gap-2">
            <Stethoscope className="w-7 h-7 text-primary-600" />
            Diagnostics & debugging
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Health checks, database counts, and live API tests using each session&apos;s stored keys (keys are never shown).
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            fetchCore();
            fetchSessions();
          }}
          disabled={loading.core}
          className="btn btn-secondary flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${loading.core ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-950/40 border border-red-800/50 rounded-lg text-red-300 text-sm">{error}</div>
      )}

      <div className="card space-y-2">
        <h2 className="text-md font-semibold text-slate-100 flex items-center gap-2">
          <Terminal className="w-5 h-5" />
          Frontend configuration
        </h2>
        <p className="text-xs text-slate-400">
          <code className="bg-slate-800 text-slate-200 px-1.5 py-0.5 rounded border border-slate-600">REACT_APP_API_URL</code> —{' '}
          <strong>{API_BASE || '(not set — using built-in default in api.js)'}</strong>
        </p>
        <p className="text-xs text-slate-400">
          Verbose HTTP logging: run{' '}
          <code className="bg-slate-800 text-slate-200 px-1.5 py-0.5 rounded border border-slate-600">localStorage.setItem(&apos;DEBUG_API&apos;, &apos;1&apos;)</code>{' '}
          in the browser console, then reload.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card">
          <h2 className="text-md font-semibold text-slate-100 mb-2 flex items-center gap-2">
            <Activity className="w-5 h-5" /> /health
          </h2>
          {health ? (
            <pre className="text-xs bg-slate-950/80 border border-slate-700 p-3 rounded overflow-x-auto">{JSON.stringify(health, null, 2)}</pre>
          ) : (
            <p className="text-sm text-slate-500">No data</p>
          )}
        </div>
        <div className="card">
          <h2 className="text-md font-semibold text-slate-100 mb-2">API docs</h2>
          <p className="text-sm text-slate-400 mb-3">Interactive OpenAPI UI (same host as your API).</p>
          <a
            href={API_BASE ? `${API_BASE.replace(/\/$/, '')}/docs` : '/docs'}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-primary-600 text-sm hover:underline"
          >
            Open Swagger <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>

      <div className="card">
        <h2 className="text-md font-semibold text-slate-100 mb-2">GET /diagnostics</h2>
        {diag ? (
          <pre className="text-xs bg-slate-950/80 border border-slate-700 p-3 rounded overflow-x-auto max-h-80">{JSON.stringify(diag, null, 2)}</pre>
        ) : (
          <p className="text-sm text-slate-500">No data</p>
        )}
      </div>

      <div className="card space-y-4">
        <h2 className="text-md font-semibold text-slate-100">Session API tests</h2>
        <p className="text-sm text-slate-400">
          Uses the OAuth token and Groq key saved for the selected session. Safe to run while the bot is stopped.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1">
            <label className="label">Session</label>
            <select
              className="input"
              value={sessionId}
              onChange={(e) => setSessionId(e.target.value)}
            >
              <option value="">Select a session</option>
              {sessions.map((s) => (
                <option key={s.session_id} value={s.session_id}>
                  {s.name || s.session_id}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            disabled={!sessionId || loading.fl}
            onClick={runFreelancerTest}
            className="btn btn-secondary"
          >
            {loading.fl ? <Loader className="w-4 h-4 animate-spin" /> : null}
            Test Freelancer OAuth
          </button>
          <button
            type="button"
            disabled={!sessionId || loading.groq}
            onClick={runGroqTest}
            className="btn btn-secondary"
          >
            {loading.groq ? <Loader className="w-4 h-4 animate-spin" /> : null}
            Test Groq API
          </button>
        </div>

        {flTest && (
          <div className="border rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-sm">Freelancer</span>
              <ResultBadge ok={flTest.ok} />
            </div>
            <pre className="text-xs bg-slate-950/80 border border-slate-700 p-2 rounded overflow-x-auto">{JSON.stringify(flTest, null, 2)}</pre>
          </div>
        )}
        {groqTest && (
          <div className="border rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-sm">Groq</span>
              <ResultBadge ok={groqTest.ok} />
            </div>
            <pre className="text-xs bg-slate-950/80 border border-slate-700 p-2 rounded overflow-x-auto">{JSON.stringify(groqTest, null, 2)}</pre>
          </div>
        )}
      </div>
    </div>
  );
}

export default Diagnostics;
